import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { StreakCounter } from './StreakCounter/StreakCounter'
import './Navigation.css'

const Navigation = ({ 
  user,
  isAuthenticated = false,
  onLogout, 
  showSearch = true, 
  showStreak = false, 
  streakCount = 0, 
  showBreadcrumb = false, 
  breadcrumbItems = [],
  onSearch,
  searchSuggestions = [],
  notificationCount = 0,
  notifications = [],
  onToggleTheme,
  currentTheme = 'light'
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = location.pathname
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const userMenuRef = useRef(null)
  const notificationRef = useRef(null)

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    if (onSearch) {
      onSearch(searchQuery)
    } else {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }
    setShowSuggestions(false)
  }

  const handleSearchInput = (value) => {
    setSearchQuery(value)
    if (onSearch && value) {
      onSearch(value)
    }
    setShowSuggestions(value.length > 0 && searchSuggestions.length > 0)
  }

  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      setSearchQuery('')
      setShowSuggestions(false)
    }
  }

  const handleLogout = () => {
    setUserMenuOpen(false)
    if (onLogout) {
      onLogout()
    }
  }

  return (
    <nav className="main-navigation" role="navigation" aria-label="Main navigation">
      <div className="container">
        {showBreadcrumb && breadcrumbItems.length > 0 && (
          <div className="breadcrumb" aria-label="Breadcrumb">
            {breadcrumbItems.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="breadcrumb-separator">/</span>}
                {item.href ? (
                  <Link to={item.href} className="breadcrumb-item">
                    {item.label}
                  </Link>
                ) : (
                  <span className="breadcrumb-item" aria-current="page">
                    {item.label}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="nav-content">
          <a 
            href="#main" 
            className="skip-link"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: '0',
              zIndex: '999',
              padding: '8px',
              background: '#000',
              color: '#fff',
              textDecoration: 'none'
            }}
            onFocus={(e) => {
              e.target.style.left = '0';
            }}
            onBlur={(e) => {
              e.target.style.left = '-9999px';
            }}
          >
            Skip to main content
          </a>
          <Link to="/" className="nav-logo" aria-label="PersonalityQuizFast">
            <span className="logo-icon" aria-hidden="true">🧠</span>
            <span className="logo-text">PersonalityQuizFast</span>
          </Link>

          {/* Desktop Navigation - Simplified for high conversion */}
          <div className="nav-links desktop-nav hidden md:flex" data-testid="desktop-nav" aria-label="Main menu">
            <Link
              to="/career"
              className={`nav-link ${currentPath.includes('/career') ? 'active' : ''}`}
              aria-current={currentPath.includes('/career') ? 'page' : undefined}
            >
              Career
            </Link>

            <Link
              to="/psychology"
              className={`nav-link ${currentPath.includes('/psychology') ? 'active' : ''}`}
              aria-current={currentPath.includes('/psychology') ? 'page' : undefined}
            >
              Psychology
            </Link>

            <Link
              to="/lifestyle"
              className={`nav-link ${currentPath.includes('/lifestyle') ? 'active' : ''}`}
              aria-current={currentPath.includes('/lifestyle') ? 'page' : undefined}
            >
              Lifestyle
            </Link>

            <Link
              to="/relationships"
              className={`nav-link ${currentPath.includes('/relationships') ? 'active' : ''}`}
              aria-current={currentPath.includes('/relationships') ? 'page' : undefined}
            >
              Relationships
            </Link>

            <Link
              to="/profile"
              className={`nav-link ${currentPath.includes('/profile') ? 'active' : ''}`}
              aria-current={currentPath.includes('/profile') ? 'page' : undefined}
            >
              🧠 Profile
            </Link>




            {/* Streak Counter - Always visible for habit reinforcement */}
            <StreakCounter 
              compact={true}
              showMilestone={false}
              className="nav-streak"
            />

            {/* Theme Toggle */}
            <button
              className="theme-toggle"
              onClick={onToggleTheme}
              aria-label="Toggle theme"
              data-testid="theme-indicator"
              data-theme={currentTheme}
            >
              {currentTheme === 'dark' ? '🌙' : '☀️'}
            </button>

            {/* Call-to-action button */}
            <button 
              className="cta-button"
              onClick={() => navigate('/psychology/quiz/personality-16')}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '20px',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginLeft: '10px'
              }}
            >
              Take Free Quiz
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label="Menu"
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`mobile-menu ${mobileMenuOpen ? '' : 'hidden'}`} 
          data-testid="mobile-menu"
          role="menu"
        >
            <div className="mobile-nav-links">
              <Link
                to="/career"
                className={`nav-link ${currentPath.includes('/career') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                role="menuitem"
              >
                💼 Career
              </Link>

              <Link
                to="/psychology"
                className={`nav-link ${currentPath.includes('/psychology') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                role="menuitem"
              >
                🧠 Psychology
              </Link>

              <Link
                to="/lifestyle"
                className={`nav-link ${currentPath.includes('/lifestyle') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                role="menuitem"
              >
                🌟 Lifestyle
              </Link>

              <Link
                to="/relationships"
                className={`nav-link ${currentPath.includes('/relationships') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                role="menuitem"
              >
                ❤️ Relationships
              </Link>

              <Link
                to="/profile"
                className={`nav-link ${currentPath.includes('/profile') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
                role="menuitem"
              >
                🧠 Profile
              </Link>

              <button
                className="nav-link cta-mobile"
                onClick={() => {
                  setMobileMenuOpen(false)
                  navigate('/psychology/quiz/personality-16')
                }}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  margin: '10px',
                  borderRadius: '20px'
                }}
              >
                ✨ Take Free Quiz Now
              </button>
            </div>
        </div>
      </div>
    </nav>
  )
}

export { Navigation };
export default Navigation